/**
 * Utilitário para exportação de avisos para imagem A5
 */

export class AnnouncementExporter {
  constructor() {
    this.canvas = null;
    this.ctx = null;
  }

  /**
   * Exporta avisos selecionados para uma imagem A5
   * @param {Array} announcements - Array com todos os avisos
   * @param {Set} selectedIds - Set com IDs dos avisos selecionados
   * @param {Function} onSuccess - Callback executado em caso de sucesso
   * @param {Function} onError - Callback executado em caso de erro
   */
  async exportToA5Image(announcements, selectedIds, onSuccess, onError) {
    if (selectedIds.size === 0) {
      onError("Selecione pelo menos um aviso para exportar");
      return;
    }

    try {
      this._initializeCanvas();
      this._setupCanvasSize();
      this._drawBackground();
      this._drawHeader();

      const selectedAnns = this._getSelectedAnnouncements(
        announcements,
        selectedIds,
      );
      this._drawAnnouncements(selectedAnns);

      this._downloadImage(selectedIds.size, onSuccess);
    } catch (error) {
      console.error("Erro ao gerar imagem:", error);
      onError("Erro ao gerar imagem");
    }
  }

  /**
   * Inicializa o canvas e contexto
   * @private
   */
  _initializeCanvas() {
    this.canvas = document.createElement("canvas");
    this.ctx = this.canvas.getContext("2d");
  }

  /**
   * Define o tamanho A5 do canvas
   * @private
   */
  _setupCanvasSize() {
    // Tamanho A5 em pixels (300 DPI para boa qualidade de impressão)
    const width = 1748; // 148mm * 300DPI / 25.4
    const height = 2480; // 210mm * 300DPI / 25.4

    this.canvas.width = width;
    this.canvas.height = height;
  }

  /**
   * Desenha o fundo branco
   * @private
   */
  _drawBackground() {
    this.ctx.fillStyle = "#FFFFFF";
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Desenha o cabeçalho
   * @private
   */
  _drawHeader() {
    const headerHeight = 200;

    // Fundo do cabeçalho
    this.ctx.fillStyle = "#575d63";
    this.ctx.fillRect(0, 0, this.canvas.width, headerHeight);

    // Texto do cabeçalho
    this.ctx.fillStyle = "white";
    this.ctx.textAlign = "center";

    // VERBO DA VIDA
    this.ctx.font = "bold 64px Arial";
    this.ctx.fillText("VERBO DA VIDA", this.canvas.width / 2, 100);

    // AVISOS IEVV-ZS
    this.ctx.font = "bold 42px Arial";
    this.ctx.fillText("AVISOS IEVV-ZS", this.canvas.width / 2, 180);

    return headerHeight;
  }

  /**
   * Filtra e ordena avisos selecionados
   * @private
   */
  _getSelectedAnnouncements(announcements, selectedIds) {
    return announcements
      .filter((ann) => selectedIds.has(ann._id))
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }

  /**
   * Desenha todos os avisos
   * @private
   */
  _drawAnnouncements(selectedAnns) {
    let yPosition = 250 + 80; // headerHeight + padding

    selectedAnns.forEach((announcement, index) => {
      yPosition = this._drawSingleAnnouncement(announcement, yPosition);

      // Verificar se passou da página
      if (yPosition > this.canvas.height - 100) {
        return; // Para de adicionar se não couber mais
      }
    });
  }

  /**
   * Desenha uma linha divisória
   * @private
   */
  _drawDividerLine(yPosition) {
    this.ctx.strokeStyle = "#DDDDDD";
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.moveTo(60, yPosition);
    this.ctx.lineTo(this.canvas.width - 60, yPosition);
    this.ctx.stroke();
  }

  /**
   * Desenha um aviso individual
   * @private
   */
  _drawSingleAnnouncement(announcement, startY) {
    let yPosition = startY;

    // Título
    this.ctx.fillStyle = "#575d63";
    this.ctx.font = "bold 50px Arial";
    this.ctx.textAlign = "center";

    const titleLines = this._wrapText(
      announcement.title.toUpperCase(),
      this.canvas.width - 120,
      20,
    );

    // Calcular altura necessária para esta seção (como no código original)
    const titleHeight = titleLines.length * 60;

    titleLines.forEach((line, lineIndex) => {
      this.ctx.fillText(
        line,
        this.canvas.width / 2,
        yPosition + lineIndex * 60,
      );
    });

    yPosition += titleHeight + 40;

    // Conteúdo
    this.ctx.fillStyle = "#333333";
    this.ctx.font = "50px Arial";
    this.ctx.textAlign = "center";

    const contentLines = this._wrapText(
      announcement.content,
      this.canvas.width - 140,
      32,
    );

    contentLines.forEach((line, lineIndex) => {
      this.ctx.fillText(
        line,
        this.canvas.width / 2,
        yPosition + lineIndex * 50,
      );
    });

    return yPosition + contentLines.length * 50 + 50; // Retorna próxima posição Y (como original)
  }

  /**
   * Quebra texto em linhas que cabem na largura especificada
   * @private
   */
  _wrapText(text, maxWidth, fontSize) {
    const words = text.split(" ");
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = this.ctx.measureText(currentLine + " " + word).width;
      if (width < maxWidth) {
        currentLine += " " + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);
    return lines;
  }

  /**
   * Faz o download da imagem gerada
   * @private
   */
  _downloadImage(totalAvisos, onSuccess) {
    this.canvas.toBlob(
      (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `avisos-ievv-zs-${new Date().toISOString().split("T")[0]}.png`;

        // Adicionar ao DOM, clicar e remover
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Limpar recursos
        URL.revokeObjectURL(url);

        // Callback de sucesso
        onSuccess(`Imagem exportada com ${totalAvisos} avisos`);
      },
      "image/png",
      0.95,
    );
  }
}

// Instância singleton para reutilização
export const announcementExporter = new AnnouncementExporter();
